import React, { useMemo, useState } from 'react';
import { Line } from '../types';
import { LINE_COLUMNS } from '../constants/columns';
import EditableCell from './EditableCell';
import ExcelFilter from './ExcelFilter';
import { useLocatorLov } from '../hooks/useLocatorLov';

interface LinesTableProps {
  lines: Line[];
  isDarkTheme?: boolean;
  onLineUpdate?: (lineId: string, field: string, value: string) => void;
  selectedHeaderToOrg?: string;
  newRowIds?: Set<string>;
  scannedLineIds?: Set<string>;
  clearErrorsRef?: React.MutableRefObject<(() => void) | null>;
  toRadFromScanFields?: string;
  radSites?: any[]; // Add radSites prop
  onEditLine?: (line: Line) => void; // Add edit line handler
  orderType?: string;
}

const LinesTable: React.FC<LinesTableProps> = ({ lines, isDarkTheme, onLineUpdate, selectedHeaderToOrg, newRowIds = new Set(), scannedLineIds = new Set(), clearErrorsRef, toRadFromScanFields, radSites = [], onEditLine, orderType }) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({
    'Serial Number': []
  });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, Record<string, string>>>({});
  const { refetch: refetchLocators } = useLocatorLov(selectedHeaderToOrg, '', false);



  // Get the organizationId to use for API calls (ToRAD logic)
  const getToOrgId = () => {

    
    if (toRadFromScanFields && radSites && radSites.length > 0) {
      const toRadSite = radSites.find(site => site.organizationName === toRadFromScanFields);
 
      if (toRadSite) {
 
        return toRadSite.organizationId;
      }
    }
    return selectedHeaderToOrg;
  };

  // Expose clear errors function to parent
  React.useEffect(() => {
    if (clearErrorsRef) {
      clearErrorsRef.current = () => setFieldErrors({});
    }
  }, [clearErrorsRef]);

  // Clear field errors when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('table') && !target.closest('input') && !target.closest('select')) {
        setFieldErrors({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  React.useEffect(() => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      lines.forEach(line => {
        if (newErrors[line.id]) {
          Object.keys(newErrors[line.id]).forEach(field => {
            const fieldMap: Record<string, keyof Line> = {
              'To Subinventory': 'toSubinventory',
              'Repair Eligibility': 'repairEligibility'
            };
            const fieldKey = fieldMap[field];
            if (fieldKey && line[fieldKey]) {
              newErrors[line.id][field] = '';
            }
          });
        }
      });
      return newErrors;
    });
  }, [lines]);

  const isLineEditable = (line: Line): boolean => {
    return false; // All lines are always readonly
  };

  const validateMandatoryField = (lineId: string, column: string, value: string): string => {
    const mandatoryFields = ['To Subinventory', 'Repair Eligibility'];
    if (mandatoryFields.includes(column) && (!value || value.trim() === '')) {
      return `${column} is mandatory and cannot be null`;
    }
    return '';
  };

  const handleLineUpdate = (lineId: string, field: string, value: string) => {
    // Map display column names to actual field names
    const fieldMap: Record<string, string> = {
      'Pallet': 'pallet',
      'Subinventory': 'subinventory',
      'Cage': 'cage',
      'Tote': 'tote'
    };
    
    const actualField = fieldMap[field] || field;
    
    // Clear error when user updates field
    if (fieldErrors[lineId]?.[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [lineId]: {
          ...prev[lineId],
          [field]: ''
        }
      }));
    }
    
    // Validate mandatory fields on blur/change
    const error = validateMandatoryField(lineId, field, value);
    if (error) {
      setFieldErrors(prev => ({
        ...prev,
        [lineId]: {
          ...prev[lineId] || {},
          [field]: error
        }
      }));
    }
    
    if (onLineUpdate) {
      onLineUpdate(lineId, actualField, value);
    }
  };

  const filteredLines = useMemo(() => {
    return lines.filter(line => {
      const serialNumberMatch = filters['Serial Number'].length === 0 || 
        filters['Serial Number'].includes(line.serialNumber);
      
      return serialNumberMatch;
    });
  }, [lines, filters]);

  const handleFilterChange = (column: string, selectedValues: string[]) => {
    setFilters(prev => ({
      ...prev,
      [column]: selectedValues
    }));
  };

  const getColumnValues = (column: string): string[] => {
    if (column === 'Serial Number') {
      return lines.map(line => line.serialNumber || '');
    }
    return [];
  };
  
  const getLineValue = (line: Line, column: string): string => {
    const fieldMap: Record<string, keyof Line> = {
      'Description': 'description',
      'Serial Number': 'serialNumber',
      'Asset Number': 'assetNumber',
      'Validated Serial Number': 'validatedSerialNumber',
      'Validated Asset Number': 'validatedAssetNumber',
      'Item Number': 'itemNumber',
      'Pallet': 'pallet',
      'Subinventory': 'toSubinventory',
      'Cage': 'cage',
      'Tote': 'tote',
      'Validated Serial Item': 'validatedSerialItem',
      'Receiving Status': 'status',
      'Tracking Number': 'trackingNumber',
      'ISO Status': 'replacementOrderLineStatus',
      'Repair Eligibility': 'repairEligibility',
      'RAD Receipt Date': 'radReceiptDate'
    };
    
    // Handle Edit column
    if (column === 'Edit') {
      return ''; // Return empty string, will be handled separately in render
    }
    
    // Handle combined Replacement Order column
    if (column === 'Replacement Order') {
      const internalSalesOrder = line.internalSalesOrder || '';
      const isoLineNumber = line.isoLineNumber || '';
      if (internalSalesOrder && isoLineNumber) {
        return `${internalSalesOrder}-${isoLineNumber}`;
      } else if (internalSalesOrder) {
        return internalSalesOrder;
      } else if (isoLineNumber) {
        return isoLineNumber;
      }
      return '';
    }
    
    // Handle RAD Receipt Date formatting
    if (column === 'RAD Receipt Date') {
      const dateValue = line.radReceiptDate;
      if (dateValue) {
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}-${day}-${year}`;
          }
        } catch (error) {
          console.error('Error formatting date:', error);
        }
      }
      return '';
    }
    
    // Handle Receiving Status column
    if (column === 'Receiving Status') {
      return String(line.status || '');
    }
    
    // Handle Repair Eligibility column
    if (column === 'Repair Eligibility') {
      const value = line[fieldMap[column]];
      if (value === 'Yes') return 'Yes';
      if (value === 'No') return 'Serial number not eligible for repair';
      return String(value || '');
    }
    
    const value = line[fieldMap[column]];
    return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value || '');
  };

  const getStatusBackgroundColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return '#d4edda';
    if (statusLower === 'error') return '#f8d7da';
    return 'transparent';
  };

  const getStatusTextColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return '#155724';
    if (statusLower === 'error') return '#721c24';
    return isDarkTheme ? '#fff' : '#000';
  };

  return (
    <>
      <style>
        {`
          .thick-scrollbar::-webkit-scrollbar {
            width: 7px;
            height: 7px;
          }
          .thick-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkTheme ? '#2a2a2a' : '#f1f1f1'};
          }
          .thick-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkTheme ? '#555' : '#888'};
            border-radius: 5px;
          }
          .thick-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkTheme ? '#777' : '#555'};
          }
        `}
      </style>
    <div 
      style={{ 
        maxHeight: '375px', 
        overflowY: 'auto', 
        overflowX: 'auto',
        border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
        position: 'relative'
      }}
      className="thick-scrollbar"
      data-tour="lines-table"
    >
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', overflow: 'visible' }}>
        <thead>
          <tr style={{ backgroundColor: isDarkTheme ? '#444' : '#f5f5f5' }}>
            {LINE_COLUMNS.map((column) => {
              const isMandatory = ['To Subinventory', 'Repair Eligibility'].includes(column);
              const hasFilter = column === 'Serial Number';
              return (
                <th key={column} style={{ 
                  padding: '8px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                  textAlign: 'left', 
                  color: isDarkTheme ? '#fff' : '#000', 
                  position: 'sticky', 
                  top: 0, 
                  backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', 
                  fontSize: '13px', 
                  zIndex: 100,
                  minWidth: column === 'Edit' ? '50px' : column === 'RAD Receipt Date' ? '100px' : column === 'Pallet' ? '150px' : column === 'Cage' ? '100px' : column === 'Tote' ? '100px' : column === 'Tracking Number' ? '150px' : 'auto',
                  width: column === 'Edit' ? '50px' : column === 'RAD Receipt Date' ? '100px' : column === 'Pallet' ? '150px' : column === 'Cage' ? '100px' : column === 'Tote' ? '100px' : column === 'Tracking Number' ? '150px' : 'auto',
                  height: '40px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      {column}
                      {isMandatory && <span style={{ color: 'black', marginLeft: '2px' }}>*</span>}
                    </span>
                    {hasFilter && (
                      <ExcelFilter
                        column={column}
                        values={getColumnValues(column)}
                        selectedValues={filters[column]}
                        onFilterChange={handleFilterChange}
                        isDarkTheme={isDarkTheme}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredLines.length === 0 ? (
            <tr style={{ backgroundColor: isDarkTheme ? '#2a2a2a' : 'white', color: isDarkTheme ? '#fff' : '#000' }}>
              {LINE_COLUMNS.map((column) => (
                <td key={column} style={{ 
                  padding: '8px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                  color: isDarkTheme ? '#fff' : '#000', 
                  fontSize: '12px'
                }}>
                  
                </td>
              ))}
            </tr>
          ) : (
            filteredLines.map((line) => (
              <tr key={line.id} style={{ 
                backgroundColor: scannedLineIds.has(line.id)
                  ? (isDarkTheme ? '#0d47a1' : '#e3f2fd') 
                  : (isDarkTheme ? '#2a2a2a' : 'white'), 
                color: isDarkTheme ? '#fff' : '#000' 
              }}>
                {LINE_COLUMNS.map((column) => {
                  const cellValue = getLineValue(line, column);
                  const isEditable = isLineEditable(line) && ['Pallet', 'Subinventory', 'Cage', 'Tote', 'Tracking Number'].includes(column);
                  const isMandatory = ['To Subinventory', 'Repair Eligibility'].includes(column);
                  const hasError = fieldErrors[line.id]?.[column];
                  const isMandatoryEmpty = isMandatory && isLineEditable(line) && (!cellValue || cellValue.trim() === '');
                  
                  return (
                    <td key={column} style={{ 
                      padding: '4px', 
                      border: `1px solid ${hasError ? '#dc3545' : (isDarkTheme ? '#555' : '#ccc')}`, 
                      backgroundColor: column === 'Receiving Status' && cellValue ? getStatusBackgroundColor(cellValue) : 'inherit',
                      color: column === 'Receiving Status' ? getStatusTextColor(cellValue) : (isDarkTheme ? '#fff' : '#000'), 
                      fontSize: '12px',
                      position: 'relative',
                      overflow: 'visible',
                      minWidth: column === 'Edit' ? '50px' : column === 'RAD Receipt Date' ? '100px' : column === 'Pallet' ? '150px' : column === 'Cage' ? '100px' : column === 'Tote' ? '100px' : column === 'Tracking Number' ? '150px' : 'auto',
                      width: column === 'Edit' ? '50px' : column === 'RAD Receipt Date' ? '100px' : column === 'Pallet' ? '150px' : column === 'Cage' ? '100px' : column === 'Tote' ? '100px' : column === 'Tracking Number' ? '150px' : 'auto',
                      whiteSpace: column === 'RAD Receipt Date' ? 'nowrap' : 'normal',
                      textAlign: column === 'Edit' ? 'center' : 'left'
                    }}>
                      {column === 'Edit' ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20px' }}>
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke={line.status?.toLowerCase() === 'success' ? (isDarkTheme ? '#666' : '#ccc') : (isDarkTheme ? '#fff' : '#000')} 
                            strokeWidth="2" 
                            style={{ 
                              cursor: line.status?.toLowerCase() === 'success' ? 'not-allowed' : 'pointer',
                              opacity: line.status?.toLowerCase() === 'success' ? 0.5 : 1
                            }}
                            onClick={() => {
                              if (line.status?.toLowerCase() !== 'success' && onEditLine) {
                                onEditLine(line);
                              }
                            }}
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </div>
                      ) : isEditable ? (
                        <div>
                          <EditableCell
                            key={`${line.id}-${column}`}
                            column={column}
                            value={cellValue}
                            lineId={line.id}
                            line={line}
                            onUpdate={handleLineUpdate}
                            isDarkTheme={isDarkTheme}
                            selectedHeaderToOrg={getToOrgId()}
                            refetchLocators={refetchLocators}
                            onToRadRequired={() => {
                              // Show error message for To RAD requirement
                              alert('To RAD is required');
                            }}
                            orderType={orderType}
                          />
                          {hasError && (
                            <div style={{
                              color: '#dc3545',
                              fontSize: '10px',
                              marginTop: '2px',
                              lineHeight: '1.2'
                            }}>
                              {hasError}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ minHeight: '16px' }}>
                          {cellValue}
                          {isMandatoryEmpty && (
                            <div style={{
                              color: '#dc3545',
                              fontSize: '10px',
                              marginTop: '2px',
                              lineHeight: '1.2'
                            }}>
                              {column} is mandatory and cannot be null
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default LinesTable;